import { supabase } from "@/integrations/supabase/client";

type ErrorLogParams = {
  action: string;
  entityType?: string;
  entityId?: string;
  details?: Record<string, unknown>;
};

/**
 * Log an error to the admin_audit_log table via the log_admin_action RPC.
 * Fire-and-forget — never blocks the UI or throws.
 *
 * NOTE: This only works for platform admins due to RLS.
 * For non-admin error logging, we write to activity_log instead.
 */
export async function logAdminError(params: ErrorLogParams) {
  try {
    await supabase.rpc("log_admin_action", {
      p_action: params.action,
      p_entity_type: params.entityType ?? null,
      p_entity_id: params.entityId ?? null,
      p_details: params.details ?? {},
    });
  } catch {
    // Silent — error logging should never cascade
  }
}

export type ErrorSource = "react" | "promise" | "api" | "browser";

/**
 * Log a frontend error for any user. Writes to activity_log which has
 * org-scoped RLS (any authenticated user can insert for their org).
 *
 * All fields beyond organizationId, userId, and error are optional to
 * preserve backwards compatibility with existing call sites.
 */
export async function logFrontendError({
  organizationId,
  userId,
  error,
  errorType,
  errorSource,
  stackTrace,
  context,
}: {
  organizationId: string;
  userId: string;
  error: string;
  errorType?: string;
  errorSource?: ErrorSource;
  stackTrace?: string;
  context?: string;
}) {
  try {
    const details: Record<string, unknown> = {
      error,
      error_type: errorType ?? null,
      error_source: errorSource ?? "browser",
      stack_trace: stackTrace ? stackTrace.slice(0, 3000) : null,
      context: context ? context.slice(0, 1000) : null,
      url: typeof window !== "undefined" ? window.location.href : null,
      timestamp: new Date().toISOString(),
    };

    if (typeof window !== "undefined") {
      details.browser_info = {
        user_agent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        platform: navigator.platform ?? "unknown",
      };
    }

    await supabase.from("activity_log").insert({
      organization_id: organizationId,
      user_id: userId,
      action: "error",
      entity_type: "frontend",
      details,
    });
  } catch {
    // Silent
  }
}

/**
 * Attempt to retrieve the current user + org ID for error logging.
 * Returns null if the user is not authenticated or no org is active.
 * Always resolves — never rejects.
 */
export async function tryGetErrorLogContext(): Promise<{ userId: string; orgId: string } | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const orgId = localStorage.getItem("vrg_active_org_id");
    if (user && orgId) return { userId: user.id, orgId };
  } catch {
    // Silent
  }
  return null;
}
